import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import okReservaLogo from '../../assets/okreserva-logo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function useLegalCompanyInfo() {
  const [company, setCompany] = useState({
    name: 'OkReserva',
    legal_name: 'OkGo S.A.S. de C.V.',
    rfc: '',
    address: '',
    city: 'Ciudad de México',
    state: 'Ciudad de México',
    zip_code: '',
    country: 'México',
    phone: '',
    email: 'hola@okgo.mx',
    support_email: 'soporte@okgo.mx',
    website: 'https://okreserva.com',
    dpa_email: 'privacidad@okgo.mx',
    last_updated: 'Abril 2026'
  });

  useEffect(() => {
    fetch(`${API_URL}/api/legal/company-info`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCompany(d))
      .catch(() => {});
  }, []);

  return company;
}

export default function LegalLayout({ title, subtitle, sections, children, lastUpdated }) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');
  const observerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/superpoderes" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
            <div className="w-px h-5 bg-gray-700" />
            <img src={okReservaLogo} alt="OkReserva" className="h-7" style={{ filter: 'brightness(0) invert(1)' }} />
          </div>
          <div className="hidden sm:flex items-center gap-6 text-sm text-gray-400">
            <Link to="/terminos" className="hover:text-white transition-colors">Términos</Link>
            <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-white transition-colors">Aviso Legal</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-black text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-3">OkReserva · Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">{title}</h1>
          <p className="text-gray-400 text-lg max-w-2xl">{subtitle}</p>
          {lastUpdated && (
            <p className="text-gray-500 text-sm mt-6">Última actualización: {lastUpdated}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Contenido</p>
              <nav className="space-y-1">
                {sections.map(s => (
                  <button key={s.id} onClick={() => scrollTo(s.id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      activeSection === s.id
                        ? 'bg-black text-white font-semibold'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                    }`}>
                    {activeSection === s.id && <div className="w-1 h-4 bg-orange-500 rounded-full flex-shrink-0" />}
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Mobile TOC */}
            <div className="lg:hidden mb-8 p-4 bg-gray-50 rounded-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">En esta página</p>
              <div className="flex flex-wrap gap-2">
                {sections.map(s => (
                  <button key={s.id} onClick={() => scrollTo(s.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-orange-500 hover:text-orange-600 transition-all">
                    {s.label} <ChevronRight className="w-3 h-3" />
                  </button>
                ))}
              </div>
            </div>
            {children}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={okReservaLogo} alt="OkReserva" className="h-6" />
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/terminos" className="hover:text-orange-500 transition-colors">Términos</Link>
            <Link to="/privacidad" className="hover:text-orange-500 transition-colors">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-orange-500 transition-colors">Aviso Legal</Link>
            <Link to="/cookies" className="hover:text-orange-500 transition-colors">Cookies</Link>
          </div>
          <p className="text-gray-400 text-sm">© 2026 OkReserva. México.</p>
        </div>
      </footer>
    </div>
  );
}

// ===== SHARED STYLED COMPONENTS =====

export function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-16 scroll-mt-24">
      <h2 className="text-2xl font-black text-gray-900 mb-6 pb-4 border-b-2 border-gray-100">{title}</h2>
      <div className="space-y-4 text-gray-600 leading-relaxed">{children}</div>
    </section>
  );
}

export function InfoTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 my-6">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {rows.map(([label, value], i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 font-semibold text-gray-700 w-2/5">{label}</td>
              <td className="px-4 py-3 text-gray-600">{value || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AlertBox({ type = 'info', children }) {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
  };
  return (
    <div className={`border rounded-xl px-5 py-4 text-sm leading-relaxed my-4 ${styles[type]}`}>
      {children}
    </div>
  );
}

export function Highlight({ children }) {
  return <strong className="font-semibold text-gray-900">{children}</strong>;
}

export function CookieTable({ cookies }) {
  return (
    <div className="overflow-x-auto my-6 rounded-xl border border-gray-200">
      <table className="w-full text-sm min-w-[600px]">
        <thead>
          <tr className="bg-gray-900 text-white">
            <th className="px-4 py-3 text-left font-semibold">Nombre</th>
            <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
            <th className="px-4 py-3 text-left font-semibold">Propósito</th>
            <th className="px-4 py-3 text-left font-semibold">Duración</th>
            <th className="px-4 py-3 text-left font-semibold">Tipo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {cookies.map((c, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 font-mono text-xs text-gray-700">{c.name}</td>
              <td className="px-4 py-3 text-gray-600">{c.provider}</td>
              <td className="px-4 py-3 text-gray-600">{c.purpose}</td>
              <td className="px-4 py-3 text-gray-600">{c.duration}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  c.type === 'Esencial' ? 'bg-green-100 text-green-700' :
                  c.type === 'Analítica' ? 'bg-blue-100 text-blue-700' :
                  c.type === 'Preferencia' ? 'bg-purple-100 text-purple-700' :
                  'bg-orange-100 text-orange-700'
                }`}>{c.type}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
